/**
 * The object the UI actually talks to.
 *
 * Owns the adapter handshake, capability discovery, the polling loop, the
 * signal store and the analysis passes, and exposes diagnostics on top. The UI
 * subscribes by signal key and never needs to know a PID number.
 */
import { Elm327, type AdapterInfo } from './protocol/elm327.js';
import {
  ObdError, assemblePayloads, bytesToHex, hexToBytes, isErrorLine, mergeSupportBitmaps, parseMode01,
  parseSupportBitmap, stripHeader,
} from './protocol/parse.js';
import { PollScheduler, type PidRequester, type SchedulerStats } from './protocol/scheduler.js';
import { parseDtcPayload, parseReadiness, type Dtc, type ReadinessResult } from './protocol/dtc.js';
import { ALL_PIDS, PID_BY_NUMBER, PID_FOR_SIGNAL, SUPPORT_BITMAP_PIDS } from './pids/registry.js';
import { SignalStore } from './signals/store.js';
import { DerivedEngine } from './signals/derived.js';
import type { GpsFix } from './signals/gps.js';
import type { FuelCalibration } from './signals/fuel.js';
import type { JourneyScope, JourneyState, JourneySummary, TripRecord } from './signals/journey.js';
import type { VehicleIdentity } from './vehicle/identity.js';
import { normalizeVin } from './vehicle/vin.js';
import { DrivingAnalyser, type DrivingEvent } from './analysis/scoring.js';
import { defaultsForFuelType, inferFuelType, type FuelInference } from './analysis/fueltype.js';
import { DEFAULT_VEHICLE, type ConnectionState, type Transport, type VehicleProfile } from './types.js';

export interface SessionEvents {
  onStateChange?: (state: ConnectionState, detail?: string) => void;
  onTrace?: (direction: 'tx' | 'rx', text: string) => void;
  onDiscovery?: (supported: ReadonlySet<number>) => void;
  onFuelInference?: (inf: FuelInference) => void;
  /** Fired when the session itself changes the profile, e.g. after detection. */
  onVehicleChange?: (v: VehicleProfile) => void;
  onError?: (err: Error) => void;
  /** A drive ended: either the engine has been off a while, or the trip was reset. */
  onTripComplete?: (trip: TripRecord) => void;
}

export interface SessionStatus {
  state: ConnectionState;
  detail?: string;
  adapter?: AdapterInfo;
  scheduler: SchedulerStats;
  supportedPidCount: number;
  ecuCount: number;
  vin?: string;
  /** What identifies the connected car; set once it has been identified. */
  identity?: VehicleIdentity;
}

export class ObdSession implements PidRequester {
  readonly store = new SignalStore();
  readonly analyser: DrivingAnalyser;
  readonly derived: DerivedEngine;

  private elm: Elm327 | null = null;
  private scheduler: PollScheduler | null = null;
  private transport: Transport | null = null;

  private _state: ConnectionState = 'disconnected';
  private _detail: string | undefined;
  private _adapter: AdapterInfo | undefined;
  private _vin: string | undefined;
  private _ecuCount = 1;
  private _expectedResponses: number | undefined;
  private _identity: VehicleIdentity | undefined;
  /** Gathered during discovery, for the identity. */
  private pidMaps: Record<string, number> = {};
  private fuelTypeCode: number | undefined;

  private readonly supported = new Set<number>();
  /** signal key -> requested interval; several keys can share one PID. */
  private readonly subscriptions = new Map<string, number>();
  private lastAnalysisAt = 0;
  /** The PID currently polled for the accelerator, and the source list it was picked from. */
  private acceleratorPid: number | undefined;
  private acceleratorVersion = -1;

  vehicle: VehicleProfile;

  constructor(vehicle: Partial<VehicleProfile> = {}, private readonly events: SessionEvents = {}) {
    this.vehicle = { ...DEFAULT_VEHICLE, ...vehicle };
    for (const p of ALL_PIDS) for (const s of p.signals) this.store.register(s);
    this.derived = new DerivedEngine(this.store, this.vehicle);
    this.derived.onTripComplete = (trip) => this.events.onTripComplete?.(trip);
    this.analyser = new DrivingAnalyser(this.vehicle);
    DrivingAnalyser.register(this.store);
  }

  // ------------------------------------------------------------------ status

  get state(): ConnectionState {
    return this._state;
  }

  get adapter(): AdapterInfo | undefined {
    return this._adapter;
  }

  get supportedPids(): ReadonlySet<number> {
    return this.supported;
  }

  status(): SessionStatus {
    return {
      state: this._state,
      detail: this._detail,
      adapter: this._adapter,
      scheduler: this.scheduler?.currentStats ?? {
        requestsPerSec: 0, valuesPerSec: 0, latencyMs: 0, errorRate: 0, activePids: 0, batching: false,
      },
      supportedPidCount: this.supported.size,
      ecuCount: this._ecuCount,
      vin: this._vin,
      identity: this._identity,
    };
  }

  /** The connected car's identity, once known. */
  get identity(): VehicleIdentity | undefined {
    return this._identity;
  }

  setVehicle(v: VehicleProfile): void {
    this.vehicle = v;
    this.derived.setVehicle(v);
    this.analyser.setVehicle(v);
    this.events.onVehicleChange?.(v);
  }

  private setState(s: ConnectionState, detail?: string): void {
    this._state = s;
    this._detail = detail;
    this.events.onStateChange?.(s, detail);
  }

  // -------------------------------------------------------------- connection

  async connect(transport: Transport): Promise<void> {
    await this.disconnect();

    // Everything learned about the last connection has to go. The session
    // outlives individual connections, so without this a reconnect -- or a
    // different adapter, or a different car -- inherits stale capabilities.
    // The expected-responses hint in particular was being applied during
    // discovery on a reconnect, before it had been re-established.
    this._adapter = undefined;
    this._expectedResponses = undefined;
    this._ecuCount = 1;
    this._vin = undefined;
    this._identity = undefined;
    this.pidMaps = {};
    this.fuelTypeCode = undefined;
    this.supported.clear();

    this.transport = transport;
    this.setState('connecting', 'Opening ' + transport.label);

    transport.onClose((reason) => {
      if (this._state !== 'disconnected') {
        this.setState('disconnected', reason ?? 'Connection lost');
        void this.teardown();
      }
    });

    try {
      await transport.connect();
    } catch (err) {
      this.setState('error', err instanceof Error ? err.message : String(err));
      throw err;
    }

    const onTrace = this.events.onTrace;
    this.elm = new Elm327(transport, onTrace ? { onTrace } : {});
    this.elm.attach();

    this.setState('handshaking', 'Configuring adapter');
    try {
      this._adapter = await this.elm.initialise();
    } catch (err) {
      this.setState('error', 'Adapter did not respond. ' + (err instanceof Error ? err.message : ''));
      throw err;
    }

    this.setState('identifying', 'Asking the car what it supports');
    await this.discoverCapabilities();
    await this.detectEcuCount();
    await this.probeMultiFrame();
    try {
      this._vin = await this.readVin();
    } catch {
      /* plenty of cars simply do not answer mode 09 */
    }
    this._identity = this.buildIdentity();

    this.scheduler = new PollScheduler(this, (pid, data) => this.applyPid(pid, data), {
      onBatch: () => this.runAnalysis(),
      onError: (e) => this.events.onError?.(e),
    });

    // Re-apply anything the UI asked for before we were connected.
    this.rebuildSchedule();
    this.scheduler.start();
    this.setState('connected');
  }

  async disconnect(): Promise<void> {
    await this.teardown();
    if (this.transport) {
      try {
        await this.transport.disconnect();
      } catch {
        /* already gone */
      }
      this.transport = null;
    }
    this.setState('disconnected');
  }

  private async teardown(): Promise<void> {
    if (this.scheduler) {
      await this.scheduler.stop();
      this.scheduler = null;
    }
    this.elm?.detach();
    this.elm = null;
  }

  // -------------------------------------------------------------- discovery

  /**
   * Walk the support bitmaps to learn which PIDs this car answers.
   *
   * Every ECU that replies is counted: a PID any of them supports can be
   * polled, and merging makes the list the same on every connection.
   */
  private async discoverCapabilities(): Promise<void> {
    this.supported.clear();
    this.pidMaps = {};
    for (const base of SUPPORT_BITMAP_PIDS) {
      let payloads;
      try {
        payloads = await this.requestPayloads([base]);
      } catch {
        break;
      }
      const map = payloads ? mergeSupportBitmaps(payloads, base) : undefined;
      if (map === undefined) break;
      this.pidMaps[base.toString(16).padStart(2, '0').toUpperCase()] = map;
      const bytes = new Uint8Array([map >>> 24, (map >>> 16) & 0xff, (map >>> 8) & 0xff, map & 0xff]);
      for (const pid of parseSupportBitmap(base, bytes)) this.supported.add(pid);
      // Bit 0 means "the next bitmap exists". Without it, continuing just
      // wastes round trips.
      if ((map & 1) === 0) break;
    }

    for (const pid of this.supported) {
      const def = PID_BY_NUMBER.get(pid);
      if (def) for (const s of def.signals) this.store.markAvailable(s.key);
    }

    this.events.onDiscovery?.(this.supported);

    // Fuel type first from PID 0x51 if the car has it, then from the PID list.
    if (this.supported.has(0x51)) {
      try {
        const r = await this.rawRequest([0x51]);
        const d = r.values.get(0x51);
        if (d) {
          this.applyPid(0x51, d);
          if ((d[0] ?? 0) > 0) this.fuelTypeCode = d[0];
        }
      } catch {
        /* non-fatal */
      }
    }
    this.applyFuelInference();
  }

  /**
   * Apply what fuel-type detection concluded.
   *
   * Detection may set the fuel type, but it must never overwrite figures the
   * driver has entered. The redline, economy shift point and volumetric
   * efficiency are seeded from the detected fuel type exactly once -- on the
   * first successful detection -- and after that they belong to the user. The
   * previous behaviour re-applied those defaults on every single connect,
   * silently discarding hand-entered values.
   */
  applyFuelInference(): FuelInference {
    const inf = inferFuelType(this.supported, this.store);
    if (!this.vehicle.fuelTypeLocked && inf.confidence > 0.4 && inf.type !== 'unknown') {
      const seed = this.vehicle.autoTuned ? {} : defaultsForFuelType(inf.type);
      this.setVehicle({ ...this.vehicle, fuelType: inf.type, autoTuned: true, ...seed });
    }
    this.events.onFuelInference?.(inf);
    return inf;
  }

  /**
   * Count responding ECUs by asking with headers on.
   *
   * Worth doing once: when only one module answers we can tell the adapter to
   * stop listening after the first reply, which removes the full timeout wait
   * from every single request.
   */
  private async detectEcuCount(): Promise<void> {
    if (!this.elm) return;
    try {
      await this.elm.send('ATH1', 2000);
      const r = await this.elm.send('0100', 5000);
      const payloads = assemblePayloads(r.lines);
      this._ecuCount = Math.max(1, payloads.length);
      await this.elm.send('ATH0', 2000);
      this._expectedResponses = this._ecuCount === 1 ? 1 : undefined;
    } catch {
      this._ecuCount = 1;
      this._expectedResponses = undefined;
    }
  }

  /**
   * Measure whether a reply spanning more than one CAN frame can be received.
   *
   * This has to wait for discovery. Probing it during the handshake means
   * guessing at PIDs, and the obvious guess -- rpm, speed and throttle -- takes
   * for granted that the car has a throttle PID. On one that does not, the ECU
   * never answers a request containing it, so a perfectly capable adapter gets
   * written off as broken and every batch is capped at a single frame for the
   * rest of the session. Asking with PIDs the car has confirmed removes the
   * guess, and going through `rawRequest` means the probe exercises exactly the
   * path real polling will take.
   */
  private async probeMultiFrame(): Promise<void> {
    const adapter = this._adapter;
    if (!this.elm || !adapter?.supportsBatch) return;
    const pids = this.multiFrameProbePids();
    if (pids.length === 0) return;
    let ok = false;
    try {
      const r = await this.rawRequest(pids);
      ok = r.missing.length === 0;
    } catch {
      ok = false;
    }
    this._adapter = { ...adapter, supportsMultiFrame: ok };
  }

  /** The smallest set of confirmed PIDs whose reply cannot fit in one frame. */
  private multiFrameProbePids(): number[] {
    const picked: number[] = [];
    for (const pid of [...this.supported].sort((a, b) => a - b)) {
      const def = PID_BY_NUMBER.get(pid);
      if (!def || !def.batchable) continue;
      picked.push(pid);
      if (predictedResponseBytes(picked) > SINGLE_FRAME_DATA_BYTES) return picked;
      // Six PIDs per request is the protocol ceiling; a car whose confirmed
      // PIDs are all short enough to fit within it cannot be probed this way.
      if (picked.length >= 6) break;
    }
    return [];
  }

  // ---------------------------------------------------------- PidRequester

  get canBatch(): boolean {
    return this._adapter?.supportsBatch ?? false;
  }

  get maxBatchSize(): number {
    // Six is the protocol ceiling. When the adapter cannot do multi-frame the
    // real limit is the response size, enforced by responseFitsOneRequest.
    return 6;
  }

  /**
   * Whether these PIDs may be asked for together.
   *
   * The reply to a mode 01 request is 0x41 followed by (pid, data...) for each
   * PID asked. A CAN frame carries seven data bytes; anything longer has to be
   * split across frames using ISO-TP, which needs the adapter to send a flow
   * control frame back. Plenty of ELM327 clones get that wrong, so when the
   * capability probe says multi-frame does not work, batches are packed to fit
   * a single frame instead.
   */
  responseFitsOneRequest(pids: number[]): boolean {
    if (this._adapter?.supportsMultiFrame) return true;
    return predictedResponseBytes(pids) <= SINGLE_FRAME_DATA_BYTES;
  }

  async requestPids(pids: number[]): Promise<{ values: Map<number, Uint8Array>; missing: number[] }> {
    return this.rawRequest(pids);
  }

  private async rawRequest(pids: number[]): Promise<{ values: Map<number, Uint8Array>; missing: number[] }> {
    const payloads = await this.requestPayloads(pids);
    return payloads ? parseMode01(payloads, pids) : { values: new Map(), missing: pids };
  }

  /** One mode 01 request, as one payload per answering ECU; null if nothing came back. */
  private async requestPayloads(pids: number[]): Promise<string[] | null> {
    const elm = this.elm;
    if (!elm) throw new ObdError('Not connected', 'adapter');

    let cmd = '01' + pids.map((p) => p.toString(16).padStart(2, '0').toUpperCase()).join('');

    // Telling the adapter how many replies to expect lets it return the moment
    // they arrive instead of sitting out the full ST timeout -- but only for a
    // reply that fits in one frame.
    //
    // With the hint appended, many clones stop listening after the first CAN
    // frame and never send the ISO-TP flow control the ECU is waiting for. The
    // ECU gives up, the adapter returns nothing at all, and the request dies of
    // timeout. Observed on an ELM327 v1.5 clone: every reply over seven bytes
    // hung, while the same requests without the hint answered fine.
    const singleFrame = predictedResponseBytes(pids) <= SINGLE_FRAME_DATA_BYTES;
    if (this._expectedResponses !== undefined && singleFrame) cmd += String(this._expectedResponses);

    // A failed poll must not cost five seconds. ST is set to ~128 ms, so even a
    // slow reply is back long before these.
    const res = await elm.send(cmd, singleFrame ? SINGLE_FRAME_TIMEOUT_MS : MULTI_FRAME_TIMEOUT_MS);
    if (res.lines.length === 0 || res.lines.every(isErrorLine)) return null;
    return assemblePayloads(res.lines);
  }

  private buildIdentity(): VehicleIdentity | undefined {
    const id: VehicleIdentity = {};
    if (this._vin) id.vin = this._vin;
    if (Object.keys(this.pidMaps).length > 0) id.pidMaps = { ...this.pidMaps };
    const protocol = this._adapter?.protocolNumber ?? 0;
    if (protocol >= 1 && protocol <= 12) id.protocol = protocol;
    if (this.fuelTypeCode !== undefined) id.fuelTypeCode = this.fuelTypeCode;
    return Object.keys(id).length > 0 ? id : undefined;
  }

  private applyPid(pid: number, data: Uint8Array): void {
    const def = PID_BY_NUMBER.get(pid);
    if (!def) return;
    const now = Date.now();
    const decoded = def.decode(data);
    for (const [key, value] of Object.entries(decoded)) this.store.update(key, value, now);
  }

  /**
   * Take in a position fix from the phone's GPS. Safe to call at any time,
   * connected or not.
   */
  ingestGps(fix: GpsFix): void {
    this.derived.ingestGps(fix, Date.now(), this._state === 'connected');
    this.store.commit();
  }

  /** Recompute derived signals and scores, then publish one store frame. */
  private runAnalysis(): void {
    const now = Date.now();
    this.derived.compute(now);
    // The accelerator source can go bad in two ways: the ECU stops answering
    // for it, or it turns out never to move. Either way, poll the next one.
    const accelPid = this.acceleratorPid;
    if (
      (accelPid !== undefined && this.scheduler?.isSuspended(accelPid)) ||
      this.derived.accelerator.version !== this.acceleratorVersion
    ) {
      this.rebuildSchedule();
    }
    const dt = this.lastAnalysisAt === 0 ? 0 : Math.min((now - this.lastAnalysisAt) / 1000, 2);
    this.lastAnalysisAt = now;
    if (dt > 0) this.analyser.update(this.store, dt, now);
    this.store.commit();
  }

  // ------------------------------------------------------------ subscriptions

  /**
   * Ask for a signal at a given rate. Several signals can share one PID, in
   * which case the PID is polled at the fastest rate anybody asked for.
   */
  subscribe(signalKey: string, intervalMs?: number): void {
    const def = PID_FOR_SIGNAL.get(signalKey);
    const interval = intervalMs ?? def?.defaultIntervalMs ?? 1000;
    this.subscriptions.set(signalKey, interval);
    this.rebuildSchedule();
  }

  unsubscribe(signalKey: string): void {
    if (this.subscriptions.delete(signalKey)) this.rebuildSchedule();
  }

  /** Replace the whole subscription set in one go. */
  setSubscriptions(entries: Iterable<[string, number]>): void {
    this.subscriptions.clear();
    for (const [k, v] of entries) this.subscriptions.set(k, v);
    this.rebuildSchedule();
  }

  subscribedInterval(signalKey: string): number | undefined {
    return this.subscriptions.get(signalKey);
  }

  /**
   * Collapse signal subscriptions down to PID subscriptions.
   *
   * Derived signals have no PID of their own, so they pull in whatever raw
   * signals they need instead.
   */
  private rebuildSchedule(): void {
    if (!this.scheduler) return;
    const wanted = new Map<number, number>();

    const want = (pid: number, ms: number): void => {
      const cur = wanted.get(pid);
      if (cur === undefined || ms < cur) wanted.set(pid, ms);
    };

    // Trip totals integrate speed over time, so speed and engine speed are read
    // at least this often whatever the screen shows. Otherwise distance stops
    // counting on a dashboard with no speedometer.
    for (const key of ['speed', 'rpm']) {
      const def = PID_FOR_SIGNAL.get(key);
      if (def && this.supported.has(def.pid)) want(def.pid, TRIP_BASELINE_MS);
    }
    // ...and so does whatever fuel flow is derived from. Without it, a tab that
    // shows no fuel signal would let distance accumulate with no litres against
    // it, and every average since the last fill-up would read low.
    for (const pid of this.fuelBaselinePids()) want(pid, TRIP_BASELINE_MS);

    this.acceleratorVersion = this.derived.accelerator.version;
    this.acceleratorPid = this.pickAcceleratorPid();

    for (const [key, interval] of this.subscriptions) {
      const def = PID_FOR_SIGNAL.get(key);
      if (def) {
        // A widget asking for a signal the car does not have must not put its
        // PID into the rotation. Derived dependencies have always been filtered
        // this way; direct subscriptions were not, so a dashboard laid out for
        // a different car kept requesting a missing PID at its full rate. That
        // costs a round trip every time on an adapter that has few to spare,
        // and on an ECU that answers a batch containing an unknown PID with
        // silence it takes the PIDs batched alongside it down too.
        if (this.supported.size > 0 && !this.supported.has(def.pid)) continue;
        want(def.pid, interval);
        continue;
      }
      for (const dep of dependenciesOf(key)) {
        if (dep === ACCELERATOR) {
          if (this.acceleratorPid !== undefined) want(this.acceleratorPid, interval);
          continue;
        }
        const depDef = PID_FOR_SIGNAL.get(dep);
        if (depDef && this.supported.has(depDef.pid)) want(depDef.pid, interval);
      }
    }

    for (const pid of this.scheduler.activePids()) {
      if (!wanted.has(pid)) this.scheduler.unsubscribe(pid);
    }
    for (const [pid, ms] of wanted) this.scheduler.subscribe(pid, ms);
  }

  /**
   * Only one accelerator source is polled -- the best one this car supports
   * that is still answering and still believed -- rather than all four.
   */
  /**
   * The cheapest set of PIDs that yields a fuel flow on this car: a direct
   * rate if it has one, else airflow, else manifold pressure for the
   * speed-density estimate.
   */
  private fuelBaselinePids(): number[] {
    for (const key of ['fuel_rate_ecu', 'fuel_rate_gs', 'cylinder_fuel_rate', 'maf']) {
      const def = PID_FOR_SIGNAL.get(key);
      if (def && this.supported.has(def.pid)) return [def.pid];
    }
    const pids: number[] = [];
    for (const key of ['map', 'intake_temp']) {
      const def = PID_FOR_SIGNAL.get(key);
      if (def && this.supported.has(def.pid)) pids.push(def.pid);
    }
    return pids;
  }

  private pickAcceleratorPid(): number | undefined {
    for (const key of this.derived.accelerator.candidates()) {
      const def = PID_FOR_SIGNAL.get(key);
      if (!def || !this.supported.has(def.pid)) continue;
      if (this.scheduler?.isSuspended(def.pid)) continue;
      return def.pid;
    }
    return undefined;
  }

  // -------------------------------------------------------------- diagnostics

  async readDtcs(): Promise<Dtc[]> {
    const out: Dtc[] = [];
    const modes = [
      { cmd: '03', status: 'stored' as const, prefix: '43' },
      { cmd: '07', status: 'pending' as const, prefix: '47' },
      { cmd: '0A', status: 'permanent' as const, prefix: '4A' },
    ];
    for (const m of modes) {
      try {
        const res = await this.command(m.cmd, 6000);
        for (const payload of assemblePayloads(res)) {
          const hex = stripHeaderFor(payload, m.prefix);
          if (!hex.startsWith(m.prefix)) continue;
          out.push(...parseDtcPayload(hexToBytes(hex.slice(2)), m.status));
        }
      } catch {
        /* a car that does not implement mode 0A should not fail the whole read */
      }
    }
    // The same code can appear as both stored and pending; keep the worse one.
    const seen = new Map<string, Dtc>();
    for (const d of out) {
      const prev = seen.get(d.code);
      if (!prev || rank(d.status) > rank(prev.status)) seen.set(d.code, d);
    }
    return [...seen.values()];
  }

  /**
   * Mode 04. This clears codes, freeze frames and readiness monitors, and on
   * many cars resets fuel trims too. The caller is responsible for confirming
   * with the user first.
   */
  async clearDtcs(): Promise<void> {
    const res = await this.command('04', 8000);
    const ok = res.some((l) => l.startsWith('44'));
    if (!ok) throw new ObdError('The ECU did not acknowledge the clear request', 'protocol');
  }

  async readReadiness(): Promise<ReadinessResult | undefined> {
    const r = await this.rawRequest([0x01]);
    const d = r.values.get(0x01);
    return d ? parseReadiness(d) : undefined;
  }

  /** Mode 09 PID 02. Comes back as 17 ASCII characters across several frames. */
  async readVin(): Promise<string | undefined> {
    const res = await this.command('0902', 6000);
    for (const payload of assemblePayloads(res)) {
      const hex = stripHeaderFor(payload, '4902');
      if (!hex.startsWith('4902')) continue;
      // After 4902 there is a one-byte message count, then the ASCII.
      const bytes = hexToBytes(hex.slice(6));
      const text = new TextDecoder().decode(bytes).replace(/[^\x20-\x7E]/g, '').trim();
      // Unprogrammed ECUs answer with zeros or blanks, which is not a VIN.
      const vin = normalizeVin(text.slice(-17));
      if (vin) return vin;
    }
    return undefined;
  }

  /** Mode 02: the snapshot the ECU stored when the first DTC was set. */
  async readFreezeFrame(pids: number[]): Promise<Map<number, Uint8Array>> {
    const out = new Map<number, Uint8Array>();
    for (const pid of pids) {
      try {
        const res = await this.command('02' + pid.toString(16).padStart(2, '0').toUpperCase() + '00', 4000);
        for (const payload of assemblePayloads(res)) {
          const hex = stripHeaderFor(payload, '42');
          if (!hex.startsWith('42')) continue;
          const def = PID_BY_NUMBER.get(pid);
          // Layout is 42, pid, frame number, then the data.
          const bytes = hexToBytes(hex.slice(6));
          out.set(pid, def ? bytes.slice(0, def.bytes) : bytes);
        }
      } catch {
        /* skip PIDs with no stored snapshot */
      }
    }
    return out;
  }

  /** Send a raw command, pausing the poll loop so the reply is not interleaved. */
  async command(cmd: string, timeoutMs?: number): Promise<string[]> {
    const elm = this.elm;
    if (!elm) throw new ObdError('Not connected', 'adapter');
    const wasRunning = this.scheduler !== null;
    if (wasRunning) await this.scheduler?.stop();
    try {
      const res = await elm.send(cmd, timeoutMs);
      return res.lines;
    } finally {
      if (wasRunning) this.scheduler?.start();
    }
  }

  get recentEvents(): readonly DrivingEvent[] {
    return this.analyser.recentEvents;
  }

  /** Totals over one span: this drive, since the last fill-up, or all time. */
  summary(scope: JourneyScope): JourneySummary {
    return this.derived.summary(scope);
  }

  /** What to show for a signal over a span; `now` is the live reading. */
  scopedValue(key: string, scope: JourneyScope | 'now'): number | undefined {
    return scope === 'now' ? this.store.value(key) : this.derived.journey.value(scope, key);
  }

  /** Start a span over, e.g. the tank span after filling up. */
  resetScope(scope: JourneyScope): void {
    this.derived.resetScope(scope);
  }

  /** End the drive in progress now, banking it, e.g. when the app is closing. */
  endTrip(): TripRecord | undefined {
    return this.derived.journey.endTrip();
  }

  exportJourney(): JourneyState {
    return this.derived.exportJourney();
  }

  importJourney(state: JourneyState | undefined): void {
    this.derived.importJourney(state);
  }

  /** Apply what recorded fill-ups say about the fuel estimate. */
  setFuelCalibration(cal: FuelCalibration | undefined): void {
    this.derived.setFuelCalibration(cal);
  }

  resetTrip(): void {
    this.derived.resetTrip();
    this.analyser.reset();
    this.store.resetStats();
  }
}

/** Slowest that speed and engine speed are ever polled, for the trip totals. */
const TRIP_BASELINE_MS = 1000;

/** Data bytes carried by one CAN frame, after the ISO-TP length byte. */
const SINGLE_FRAME_DATA_BYTES = 7;
const SINGLE_FRAME_TIMEOUT_MS = 1500;
const MULTI_FRAME_TIMEOUT_MS = 3000;

/** How many data bytes a mode 01 reply for these PIDs will contain. */
export function predictedResponseBytes(pids: number[]): number {
  let total = 1; // the 0x41 mode echo
  for (const pid of pids) {
    const def = PID_BY_NUMBER.get(pid);
    // An unknown PID could be any length; assume the worst so it goes alone.
    total += 1 + (def?.bytes ?? SINGLE_FRAME_DATA_BYTES);
  }
  return total;
}

const rank = (s: Dtc['status']): number => (s === 'permanent' ? 3 : s === 'stored' ? 2 : 1);

/** Like `stripHeader` but for arbitrary mode prefixes rather than mode 01. */
function stripHeaderFor(hex: string, prefix: string): string {
  if (hex.startsWith(prefix)) return hex;
  for (const skip of [4, 6, 8, 10, 16, 18]) {
    if (hex.length > skip && hex.startsWith(prefix, skip)) return hex.slice(skip);
  }
  return hex;
}

/**
 * Stands for whichever pedal or throttle PID the accelerator is being read
 * from, which depends on what the car supports.
 */
export const ACCELERATOR = '@accelerator';

/**
 * Which raw signals a derived signal needs.
 *
 * Kept here rather than on the definitions so that adding a widget for, say,
 * "boost" automatically starts polling MAP and barometric pressure.
 */
const DERIVED_DEPENDENCIES: Record<string, string[]> = {
  boost: ['map', 'baro'],
  boost_bar: ['map', 'baro'],
  maf_effective: ['maf', 'map', 'rpm', 'intake_temp'],
  fuel_rate: ['fuel_rate_ecu', 'fuel_rate_gs', 'cylinder_fuel_rate', 'maf', 'map', 'rpm', 'intake_temp', 'lambda_commanded', 'o2_s1_lambda', 'engine_load', 'stft_b1', 'ltft_b1'],
  fuel_source: ['fuel_rate_ecu', 'maf'],
  consumption: ['speed', 'fuel_rate_ecu', 'maf', 'map', 'rpm', 'intake_temp', 'engine_load'],
  consumption_avg: ['speed', 'fuel_rate_ecu', 'maf', 'map', 'rpm', 'engine_load'],
  air_fuel_ratio: ['lambda_commanded', 'o2_s1_lambda', 'stft_b1', 'ltft_b1'],
  accelerator: [ACCELERATOR],
  road_speed: ['speed'],
  speed_sensor_error: ['speed'],
  accel_long: ['speed'],
  accel_g: ['speed'],
  gear: ['rpm', 'speed', ACCELERATOR],
  gear_ratio: ['rpm', 'speed'],
  torque_nm: ['torque_actual', 'torque_reference', 'rpm'],
  power_kw: ['torque_actual', 'torque_reference', 'rpm', 'maf'],
  power_hp: ['torque_actual', 'torque_reference', 'rpm', 'maf'],
  throttle_rate: [ACCELERATOR],
  rpm_rate: ['rpm'],
  engine_load_pct: ['absolute_load', 'engine_load'],
  coolant_warm: ['coolant_temp'],
  fuel_remaining: ['fuel_level'],
  range_km: ['fuel_level', 'speed', 'maf'],
  trip_distance: ['speed', 'rpm'],
  trip_distance_gps: ['speed'],
  trip_fuel: ['speed', 'rpm', 'maf', 'fuel_rate_ecu'],
  trip_duration: ['rpm'],
  trip_avg_speed: ['speed', 'rpm'],
  trip_idle_pct: ['speed', 'rpm'],
  trip_coast_pct: ['speed', 'rpm', ACCELERATOR],
  score_overall: ['speed', 'rpm', ACCELERATOR, 'coolant_temp', 'maf'],
  score_smoothness: ['speed', 'rpm', ACCELERATOR],
  score_economy: ['speed', 'maf', ACCELERATOR, 'rpm'],
  score_anticipation: ['speed'],
  score_engine_care: ['rpm', 'coolant_temp', 'speed'],
  aggression: ['speed', 'rpm', ACCELERATOR],
  shift_advice: ['rpm', 'speed', ACCELERATOR],
};

export function dependenciesOf(signalKey: string): string[] {
  return DERIVED_DEPENDENCIES[signalKey] ?? [];
}

export { bytesToHex, hexToBytes };
